import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Person as PersonIcon,
  PhotoCamera as CameraIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { getFirestore, collection, addDoc, getDocs, deleteDoc, doc, query, where, updateDoc, setDoc } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import app from '../config/firebase';
import AdminProfileImageService from '../services/AdminProfileImageService';

// Types
interface AdminProfile {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  department: string;
  joinDate: string;
  role: string;
  profileImage?: string;
}

interface AdminUser {
  id: string;
  email: string;
  fullName?: string;
  role: string;
  addedBy: string;
  addedDate: string;
  status: 'pending' | 'active';
  temporaryPassword?: string;
  passwordChanged?: boolean;
  phoneNumber?: string;
  department?: string;
  createdAt?: string;
}

const AdminProfile: React.FC = () => {
  const db = getFirestore(app);
  const auth = getAuth(app);
  const location = useLocation();
  
  // State
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showAddAdminDialog, setShowAddAdminDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    department: '',
    role: ''
  });

  // New admin form state
  const [newAdminData, setNewAdminData] = useState({
    email: '',
    role: 'Admin'
  });

  // Effects - Reload profile when location changes (including after login)
  useEffect(() => {
    console.log('🔄 AdminProfile mounted or location changed');
    loadProfile();
    loadAdminUsers();
    
    // Listen for custom admin data update events
    const handleAdminUpdate = () => {
      console.log('Admin data updated, reloading profile and admin list...');
      loadProfile();
      loadAdminUsers(); // Also reload admin users list
    };
    
    window.addEventListener('adminDataUpdated', handleAdminUpdate);
    return () => window.removeEventListener('adminDataUpdated', handleAdminUpdate);
  }, [location.pathname]);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      console.log('🔄 Starting profile load...');
      
      // Try multiple sources for admin email
      let adminEmail = localStorage.getItem('adminEmail');
      
      // Fallback 1: Check auth.currentUser
      if (!adminEmail && auth.currentUser) {
        adminEmail = auth.currentUser.email;
        console.log('📧 Using email from auth.currentUser:', adminEmail);
        if (adminEmail) {
          localStorage.setItem('adminEmail', adminEmail);
        }
      }
      
      // Fallback 2: Check adminData in localStorage
      if (!adminEmail) {
        const adminDataStr = localStorage.getItem('adminData');
        if (adminDataStr) {
          try {
            const adminData = JSON.parse(adminDataStr);
            adminEmail = adminData.email;
            console.log('📧 Using email from adminData:', adminEmail);
          } catch (e) {
            console.error('Failed to parse adminData:', e);
          }
        }
      }
      
      if (!adminEmail) {
        console.error('❌ No admin email found in any source');
        showNotification('Session expired. Please login again.', 'error');
        setIsLoading(false);
        // Set a default empty profile to prevent infinite loading
        setProfile(null);
        return;
      }

      console.log('✅ Loading profile for email:', adminEmail);

      // Query Firestore for current admin's data
      const adminsRef = collection(db, 'adminUsers');
      const q = query(adminsRef, where('email', '==', adminEmail));
      
      console.log('📡 Querying Firestore for admin data...');
      const snapshot = await getDocs(q);
      console.log('📡 Firestore query completed, docs found:', snapshot.size);
      
      if (snapshot.empty) {
        console.error('❌ Admin not found in database for email:', adminEmail);
        showNotification('Admin account not found. Please contact administrator.', 'error');
        setIsLoading(false);
        setProfile(null);
        return;
      }

      const adminDoc = snapshot.docs[0];
      const adminData = adminDoc.data();
      
      console.log('📊 Raw Firestore admin data:', adminData);
      console.log('📊 Firestore admin profile data loaded:', {
        hasData: !!adminData,
        hasProfileImage: !!adminData.profileImage,
        profileImageUrl: adminData.profileImage,
        name: adminData.fullName || adminData.name,
        email: adminData.email,
        isCloudUrl: adminData.profileImage ? AdminProfileImageService.isCloudImageUrl(adminData.profileImage) : false,
        allKeys: Object.keys(adminData)
      });
      
      // Extract name from email if fullName not available
      const emailName = adminEmail.split('@')[0].replace(/[._-]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Set profile data from Firestore - prioritize fullName field, ensure all fields have values
      const currentProfile: AdminProfile = {
        id: adminDoc.id,
        fullName: adminData.fullName || adminData.name || emailName || 'Admin User',
        email: adminData.email || adminEmail,
        phoneNumber: adminData.phoneNumber || '+91-XXXXXXXXXX',
        department: adminData.department || 'Administration',
        joinDate: adminData.createdAt?.split('T')[0] || adminData.addedDate?.split('T')[0] || adminData.joinDate || new Date().toISOString().split('T')[0],
        role: adminData.role || 'Administrator',
        profileImage: adminData.profileImage || ''
      };

      console.log('✅ Profile object created:', currentProfile);
      console.log('✅ Setting profile state with:', JSON.stringify(currentProfile, null, 2));

      setProfile(currentProfile);
      console.log('✅ Profile state updated');
      console.log('✅ Profile state updated');
      setFormData({
        fullName: currentProfile.fullName,
        email: currentProfile.email,
        phoneNumber: currentProfile.phoneNumber,
        department: currentProfile.department,
        role: currentProfile.role
      });
      console.log('✅ Form data updated');
      
      // Update localStorage with latest data to sync across components
      localStorage.setItem('adminData', JSON.stringify({
        email: currentProfile.email,
        fullName: currentProfile.fullName,
        role: currentProfile.role
      }));
      
      console.log('✅ Profile loading completed successfully');
      setIsLoading(false);
      
    } catch (error: any) {
      console.error('❌ Error loading admin profile:', error);
      console.error('Error details:', error?.message);
      console.error('Error stack:', error?.stack);
      showNotification('Failed to load profile. Please refresh the page.', 'error');
      setIsLoading(false);
    }
  };

  const loadAdminUsers = async () => {
    try {
      console.log('📋 Loading admin users list...');
      const adminsRef = collection(db, 'adminUsers');
      const snapshot = await getDocs(adminsRef);
      const users: AdminUser[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Admin user:', doc.id, data.email, 'passwordChanged:', data.passwordChanged);
        users.push({ id: doc.id, ...data } as AdminUser);
      });
      
      console.log('✅ Loaded', users.length, 'admin users');
      setAdminUsers(users);
    } catch (error) {
      console.error('❌ Error loading admin users:', error);
    }
  };

  // Functions
  const showNotification = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleEditProfile = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    if (profile) {
      setFormData({
        fullName: profile.fullName,
        email: profile.email,
        phoneNumber: profile.phoneNumber,
        department: profile.department,
        role: profile.role
      });
    }
  };

  const handleSaveProfile = () => {
    if (!profile) return;
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setProfile({
        ...profile,
        ...formData
      });
      setEditMode(false);
      setIsLoading(false);
      showNotification('Profile updated successfully!', 'success');
    }, 1000);
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('📸 Admin image selected:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    setSelectedImageFile(file);
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreviewUrl(previewUrl);
    
    console.log('✅ Image preview created');
  };

  const handleUploadProfileImage = async () => {
    if (!selectedImageFile || !profile) {
      showNotification('Please select an image first', 'error');
      return;
    }

    setUploadingImage(true);
    
    try {
      console.log('☁️ Starting admin profile image upload to Firebase Storage...');
      
      // Upload to Firebase Storage and get cloud URL
      const cloudImageUrl = await AdminProfileImageService.uploadProfileImage(
        selectedImageFile,
        profile.id
      );
      
      console.log('✅✅✅ CLOUD IMAGE URL READY FOR SAVING:', cloudImageUrl);
      console.log('🔍 Is cloud URL?', AdminProfileImageService.isCloudImageUrl(cloudImageUrl));
      
      // Update Firestore with cloud image URL - use adminUsers collection
      const adminRef = doc(db, 'adminUsers', profile.id);
      await updateDoc(adminRef, {
        profileImage: cloudImageUrl,
        updatedAt: new Date().toISOString()
      });
      
      console.log('💾💾💾 SAVED TO DATABASE - PROFILE IMAGE:', cloudImageUrl);
      console.log('✅ Admin profile image updated in Firestore (adminUsers collection)');
      
      // Update local state
      setProfile({
        ...profile,
        profileImage: cloudImageUrl
      });
      
      // Clean up
      setShowImageDialog(false);
      setSelectedImageFile(null);
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      setImagePreviewUrl(null);
      
      showNotification('Profile image updated successfully! Image will sync across all devices.', 'success');
    } catch (error: any) {
      console.error('❌ Admin profile image upload failed:', error);
      showNotification(`Failed to upload image: ${error.message}`, 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const generateTemporaryPassword = (): string => {
    // Generate a random 10-character password
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleAddAdmin = async () => {
    if (!newAdminData.email || !profile) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newAdminData.email)) {
      showNotification('Please enter a valid email format', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const adminsRef = collection(db, 'adminUsers');
      
      // Check if admin already exists
      const q = query(adminsRef, where('email', '==', newAdminData.email));
      const existingAdmins = await getDocs(q);
      
      if (!existingAdmins.empty) {
        showNotification('Admin with this email already exists', 'error');
        setIsLoading(false);
        return;
      }

      // Generate temporary password
      const tempPassword = generateTemporaryPassword();
      
      // Extract name from email
      const emailName = newAdminData.email.split('@')[0].replace(/[._-]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // STEP 1: Create Firebase Authentication user
      console.log('Creating Firebase Auth user...');
      const userCredential = await createUserWithEmailAndPassword(auth, newAdminData.email, tempPassword);
      const firebaseUser = userCredential.user;
      
      console.log('✅ Firebase Auth user created with temporary password:', firebaseUser.uid);

      // STEP 2: Create user profile in users collection
      console.log('Creating user profile...');
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        name: emailName,
        email: newAdminData.email,
        role: 'admin',
        phone: '',
        isAdmin: true,
        permissions: ['all'],
        department: 'Administration',
        position: newAdminData.role,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      console.log('✅ User profile created');

      // STEP 3: Create admin record in adminUsers collection with complete profile data
      const newAdmin: Omit<AdminUser, 'id'> = {
        email: newAdminData.email,
        fullName: emailName,
        role: newAdminData.role,
        addedBy: profile.email,
        addedDate: new Date().toISOString(),
        status: 'active',
        temporaryPassword: tempPassword,
        passwordChanged: false,
        // Add default profile fields for immediate profile loading
        phoneNumber: '+91-XXXXXXXXXX',
        department: 'Administration',
        createdAt: new Date().toISOString()
      };

      const docRef = await addDoc(adminsRef, newAdmin);
      
      console.log('✅ Admin record created in adminUsers collection');
      
      setAdminUsers([...adminUsers, { id: docRef.id, ...newAdmin }]);
      setNewAdminData({ email: '', role: 'Admin' });
      setShowAddAdminDialog(false);
      
      // Show password to copy
      alert(
        `Admin created successfully!\n\n` +
        `📧 Email: ${newAdminData.email}\n` +
        `🔑 Temporary Password: ${tempPassword}\n\n` +
        `✅ The admin can now login using "First Time Login" with:\n` +
        `• Email: ${newAdminData.email}\n` +
        `• Temporary Password: ${tempPassword}\n\n` +
        `📝 They will be prompted to set a new password on first login.\n` +
        `Share these credentials with the new admin.`
      );
      
      showNotification('Admin created successfully! Share the temporary password for first-time login.', 'success');
    } catch (error: any) {
      console.error('Error creating admin:', error);
      
      // Handle specific Firebase auth errors
      if (error.code === 'auth/email-already-in-use') {
        showNotification('Email already in use. Please choose a different email.', 'error');
      } else if (error.code === 'auth/weak-password') {
        showNotification('Password is too weak. Please try again.', 'error');
      } else if (error.code === 'auth/invalid-email') {
        showNotification('Invalid email address format.', 'error');
      } else {
        showNotification('Failed to create admin. Please try again.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAdmin = async (adminId: string) => {
    if (!window.confirm('Are you sure you want to remove this admin?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'adminUsers', adminId));
      setAdminUsers(adminUsers.filter(admin => admin.id !== adminId));
      showNotification('Admin removed successfully', 'success');
    } catch (error) {
      console.error('Error deleting admin:', error);
      showNotification('Failed to remove admin', 'error');
    }
  };

  const departments = [
    'Hospital Management',
    'Medical Administration',
    'IT Department',
    'Finance Department',
    'Human Resources',
    'Operations'
  ];

  const roles = [
    'Administrator',
    'Super Admin',
    'Department Head',
    'Manager',
    'Supervisor'
  ];

  // Debug: Log render state
  console.log('🎨 AdminProfile render - profile:', profile ? 'exists' : 'null', 'isLoading:', isLoading);
  
  if (!profile || isLoading) {
    console.log('🔄 Showing loading screen - profile:', !!profile, 'isLoading:', isLoading);
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Loading profile...
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {isLoading ? 'Fetching data...' : 'Initializing...'}
        </Typography>
      </Box>
    );
  }

  console.log('✅ Rendering full profile page for:', profile.email);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1A202C', mb: 1 }}>
            Admin Profile
          </Typography>
          <Typography variant="body1" sx={{ color: '#718096' }}>
            Manage admin account settings and permissions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={editMode ? handleSaveProfile : handleEditProfile}
          disabled={isLoading}
          sx={{ 
            backgroundColor: '#4A90E2',
            '&:hover': { backgroundColor: '#357ABD' }
          }}
        >
          {isLoading ? 'Saving...' : editMode ? 'Save Changes' : 'Edit Profile'}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Profile Information Card */}
        <Box sx={{ flex: { md: '0 0 400px' } }}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              {/* Profile Image */}
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                <Avatar
                  src={profile.profileImage && AdminProfileImageService.isCloudImageUrl(profile.profileImage) 
                    ? profile.profileImage 
                    : undefined}
                  sx={{
                    width: 120,
                    height: 120,
                    backgroundColor: '#4A90E2',
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    border: '4px solid #E2E8F0'
                  }}
                >
                  {!profile.profileImage || !AdminProfileImageService.isCloudImageUrl(profile.profileImage) 
                    ? profile.fullName.charAt(0) 
                    : undefined}
                </Avatar>
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: '#EF4444',
                    color: 'white',
                    width: 36,
                    height: 36,
                    '&:hover': { backgroundColor: '#DC2626' }
                  }}
                  onClick={() => setShowImageDialog(true)}
                >
                  <CameraIcon />
                </IconButton>
              </Box>

              {/* Profile Name and Role */}
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                {profile.fullName}
              </Typography>
              <Typography variant="body1" sx={{ color: '#718096', mb: 2 }}>
                {profile.role}
              </Typography>
              
              {/* Admin Badge */}
              <Chip
                icon={<AdminIcon />}
                label="System Administrator"
                sx={{
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
            </CardContent>
          </Card>
        </Box>

        {/* Personal Information */}
        <Box sx={{ flex: 1 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1, color: '#4A90E2' }} />
                Personal Information
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096', mb: 1 }}>
                    Full Name
                  </Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      size="small"
                    />
                  ) : (
                    <Typography variant="body1" sx={{ py: 1 }}>
                      {profile.fullName}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096', mb: 1 }}>
                    Email Address
                  </Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      size="small"
                    />
                  ) : (
                    <Typography variant="body1" sx={{ py: 1 }}>
                      {profile.email}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096', mb: 1 }}>
                    Phone Number
                  </Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                      size="small"
                    />
                  ) : (
                    <Typography variant="body1" sx={{ py: 1 }}>
                      {profile.phoneNumber}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096', mb: 1 }}>
                    Department
                  </Typography>
                  {editMode ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      >
                        {departments.map((dept) => (
                          <MenuItem key={dept} value={dept}>
                            {dept}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1" sx={{ py: 1 }}>
                      {profile.department}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096', mb: 1 }}>
                    Role
                  </Typography>
                  {editMode ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      >
                        {roles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1" sx={{ py: 1 }}>
                      {profile.role}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096', mb: 1 }}>
                    Join Date
                  </Typography>
                  <Typography variant="body1" sx={{ py: 1 }}>
                    {new Date(profile.joinDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                </Box>
              </Box>

              {editMode && (
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveProfile}
                    disabled={isLoading}
                    sx={{ backgroundColor: '#4A90E2' }}
                  >
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mt: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Add Admin Section */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <PersonAddIcon sx={{ mr: 1, color: '#4A90E2' }} />
                  Admin Management
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setShowAddAdminDialog(true)}
                  sx={{ 
                    backgroundColor: '#4A90E2',
                    '&:hover': { backgroundColor: '#357ABD' }
                  }}
                >
                  Add New Admin
                </Button>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F7FAFC' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Temp Password</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Added Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Added By</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adminUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#718096' }}>
                          No additional admins added yet. Click "Add New Admin" to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      adminUsers.map((admin) => (
                        <TableRow key={admin.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <EmailIcon sx={{ mr: 1, color: '#718096', fontSize: '1.2rem' }} />
                              {admin.email}
                            </Box>
                          </TableCell>
                          <TableCell>{admin.fullName || 'Not set'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={admin.role} 
                              size="small"
                              sx={{ 
                                backgroundColor: '#E0F2FE',
                                color: '#0369A1',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Chip 
                                label={admin.passwordChanged ? 'Active' : 'Pending Setup'}
                                size="small"
                                sx={{ 
                                  backgroundColor: admin.passwordChanged ? '#D1FAE5' : '#FEF3C7',
                                  color: admin.passwordChanged ? '#065F46' : '#92400E',
                                  fontWeight: 'bold'
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            {!admin.passwordChanged && admin.temporaryPassword ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ 
                                  fontFamily: 'monospace', 
                                  backgroundColor: '#F3F4F6', 
                                  px: 1, 
                                  py: 0.5, 
                                  borderRadius: 1,
                                  fontWeight: 'bold',
                                  color: '#059669'
                                }}>
                                  {admin.temporaryPassword}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => navigator.clipboard.writeText(admin.temporaryPassword || '')}
                                  sx={{ color: '#059669' }}
                                >
                                  📋
                                </IconButton>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#718096' }}>
                                {admin.passwordChanged ? 'Password set' : 'No temp password'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {new Date(admin.addedDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </TableCell>
                          <TableCell>{admin.addedBy}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteAdmin(admin.id)}
                              sx={{ 
                                color: '#EF4444',
                                '&:hover': { backgroundColor: '#FEE2E2' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 3, p: 2, backgroundColor: '#EFF6FF', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#1E40AF', display: 'flex', alignItems: 'center' }}>
                  <SecurityIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                  <strong>Note:</strong>&nbsp;New admins will receive access to login using their email. 
                  They must create their profile during first login based on the permissions set by the primary admin.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Add Admin Dialog */}
      <Dialog 
        open={showAddAdminDialog} 
        onClose={() => !isLoading && setShowAddAdminDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#4A90E2', 
          color: 'white',
          display: 'flex',
          alignItems: 'center'
        }}>
          <PersonAddIcon sx={{ mr: 1 }} />
          Add New Administrator
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={newAdminData.email}
              onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
              placeholder="admin@example.com"
              disabled={isLoading}
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: '#718096' }} />
              }}
              helperText="Enter the email address of the new administrator"
            />

            <FormControl fullWidth>
              <InputLabel>Admin Role</InputLabel>
              <Select
                value={newAdminData.role}
                label="Admin Role"
                onChange={(e) => setNewAdminData({ ...newAdminData, role: e.target.value })}
                disabled={isLoading}
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Super Admin">Super Admin</MenuItem>
                <MenuItem value="Department Head">Department Head</MenuItem>
                <MenuItem value="Manager">Manager</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ p: 2, backgroundColor: '#F0F9FF', borderRadius: 1, border: '1px solid #BAE6FD' }}>
              <Typography variant="body2" sx={{ color: '#0C4A6E' }}>
                <strong>What happens next?</strong>
                <br />
                • The new admin will be able to login using their email
                <br />
                • They will be prompted to create their profile on first login
                <br />
                • Profile details will be based on your admin configuration
                <br />
                • You can manage their access anytime from this page
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setShowAddAdminDialog(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddAdmin}
            disabled={isLoading || !newAdminData.email}
            sx={{ 
              backgroundColor: '#4A90E2',
              '&:hover': { backgroundColor: '#357ABD' }
            }}
          >
            {isLoading ? 'Adding...' : 'Add Administrator'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Image Upload Dialog */}
      <Dialog open={showImageDialog} onClose={() => {
        setShowImageDialog(false);
        setSelectedImageFile(null);
        if (imagePreviewUrl) {
          URL.revokeObjectURL(imagePreviewUrl);
        }
        setImagePreviewUrl(null);
      }}>
        <DialogTitle>Update Profile Image</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, textAlign: 'center', minWidth: 300 }}>
            <Typography variant="body2" sx={{ color: '#718096', mb: 2 }}>
              Choose a new profile image (will sync across all devices)
            </Typography>
            
            {/* Image Preview */}
            {imagePreviewUrl && (
              <Box sx={{ mb: 2 }}>
                <Avatar
                  src={imagePreviewUrl}
                  sx={{
                    width: 120,
                    height: 120,
                    margin: '0 auto',
                    border: '3px solid #4A90E2'
                  }}
                />
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#10B981' }}>
                  Preview
                </Typography>
              </Box>
            )}
            
            <Button
              variant="outlined"
              component="label"
              startIcon={<CameraIcon />}
              sx={{ mb: 2 }}
              disabled={uploadingImage}
            >
              {selectedImageFile ? 'Change Image' : 'Select Image'}
              <input 
                type="file" 
                hidden 
                accept="image/*" 
                onChange={handleImageSelect}
              />
            </Button>
            
            {selectedImageFile && (
              <Typography variant="caption" sx={{ display: 'block', color: '#718096' }}>
                Selected: {selectedImageFile.name}
              </Typography>
            )}
            
            {uploadingImage && (
              <Typography variant="body2" sx={{ color: '#4A90E2', mt: 2 }}>
                ☁️ Uploading to cloud storage...
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowImageDialog(false);
              setSelectedImageFile(null);
              if (imagePreviewUrl) {
                URL.revokeObjectURL(imagePreviewUrl);
              }
              setImagePreviewUrl(null);
            }}
            disabled={uploadingImage}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleUploadProfileImage}
            disabled={!selectedImageFile || uploadingImage}
          >
            {uploadingImage ? 'Uploading...' : 'Upload & Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminProfile;